import { useMemo, useState } from 'react';
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Check,
  ChevronsUpDown,
  Copy,
  Download,
  ExternalLink,
  FilterX,
  Printer,
  RefreshCw,
  Settings2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { legacyRoutes } from '@/lib/legacy';
import type {
  InstallationQualification,
  InstallationQualificationAccountOption,
  InstallationQualificationsFilters,
} from '../types';

interface ColumnDef {
  key: string;
  label: string;
  defaultVisible?: boolean;
  sortKey?: InstallationQualificationsFilters['sortKey'];
  filterType?: 'text' | 'date-range' | 'select' | 'account-search';
  filterOptions?: Array<{ label: string; value: string }>;
  getValue: (item: InstallationQualification) => string | number | null;
  render?: (item: InstallationQualification) => React.ReactNode;
  noFilter?: boolean;
  align?: 'left' | 'center' | 'right';
  className?: string;
}

const STATE_OPTIONS = [
  { label: 'In Progress', value: 'inprogress' },
  { label: 'Complete', value: 'complete' },
  { label: 'Validated', value: 'validated' },
];

const TYPE_OPTIONS = [
  { label: 'Sensor', value: 'sensor' },
  { label: 'Gateway', value: 'gateway' },
];

const COLUMNS: ColumnDef[] = [
  {
    key: 'reportNumber',
    label: 'Report Number',
    sortKey: 'reportNumber',
    filterType: 'text',
    getValue: (item) => item.reportNumber,
  },
  {
    key: 'organisation',
    label: 'Organisation',
    sortKey: 'organisationName',
    filterType: 'account-search',
    getValue: (item) => {
      if (item.accountName && item.accountNumber) return `${item.accountNumber} ${item.accountName}`;
      return item.accountName || item.organisationName || '';
    },
    render: (item) => {
      if (item.accountName && item.accountNumber) {
        return (
          <span>
            <span className="font-semibold">{item.accountNumber}</span> {item.accountName}
          </span>
        );
      }
      return item.accountName || item.organisationName || '—';
    },
  },
  {
    key: 'sensor',
    label: 'Sensor',
    sortKey: 'sensorName',
    filterType: 'text',
    getValue: (item) => item.sensorName || '',
  },
  {
    key: 'transmitter',
    label: 'Transmitter',
    sortKey: 'transmitterName',
    filterType: 'text',
    getValue: (item) => item.transmitterName || item.transmitterDevId || '',
    render: (item) => item.transmitterName || item.transmitterDevId || '—',
  },
  {
    key: 'responsible',
    label: 'Responsible',
    sortKey: 'responsibleName',
    filterType: 'text',
    getValue: (item) => item.responsibleName || '',
  },
  {
    key: 'reportedAt',
    label: 'Created At',
    sortKey: 'reportedAt',
    filterType: 'date-range',
    getValue: (item) => item.reportedAt,
    render: (item) => new Date(item.reportedAt).toLocaleDateString('en-CA'),
  },
  {
    key: 'verifiedBy',
    label: 'Verified By',
    sortKey: 'verifiedByName',
    filterType: 'text',
    getValue: (item) => item.verifiedByName || '',
  },
  {
    key: 'verifiedAt',
    label: 'Verified At',
    sortKey: 'verifiedAt',
    filterType: 'date-range',
    getValue: (item) => item.verifiedAt || '',
    render: (item) => item.verifiedAt ? new Date(item.verifiedAt).toLocaleDateString('en-CA') : '—',
  },
  {
    key: 'state',
    label: 'State',
    sortKey: 'state',
    filterType: 'select',
    filterOptions: STATE_OPTIONS,
    getValue: (item) => item.state,
    render: (item) => <Badge variant={item.state === 'validated' ? 'success' : item.state === 'complete' ? 'info' : 'secondary'}>{item.state}</Badge>,
  },
  {
    key: 'qualificationType',
    label: 'Type',
    defaultVisible: false,
    filterType: 'select',
    filterOptions: TYPE_OPTIONS,
    getValue: (item) => item.qualificationType,
  },
  {
    key: 'quoteNumber',
    label: 'Quote Number',
    defaultVisible: false,
    filterType: 'text',
    getValue: (item) => item.quoteNumber || '',
  },
  {
    key: 'ticketNumber',
    label: 'Ticket Number',
    defaultVisible: false,
    filterType: 'text',
    getValue: (item) => item.ticketNumber || '',
  },
  {
    key: 'actions',
    label: 'Reports',
    noFilter: true,
    getValue: () => '',
    render: (item) => (
      <div className="flex items-center justify-end gap-2">
        {item.state !== 'inprogress' && (
          <Button
            size="sm"
            variant="outline"
            onClick={(event) => {
              event.stopPropagation();
              window.open(`/api/v4/legacy/v2/installationqualification/${item.mongoInstallationQualificationId}/html?pdf=true`, '_blank');
            }}
          >
            <Printer className="mr-1 h-3.5 w-3.5" />
            Print
          </Button>
        )}
        <Button
          size="sm"
          variant="outline"
          onClick={(event) => {
            event.stopPropagation();
            window.open(legacyRoutes.installationQualification(item.mongoInstallationQualificationId), '_blank');
          }}
        >
          <ExternalLink className="mr-1 h-3.5 w-3.5" />
          View
        </Button>
      </div>
    ),
    align: 'right',
    className: 'w-[180px]',
  },
];

const VISIBILITY_STORAGE_KEY = 'atek-installation-qualifications-column-visibility';

function getDefaultVisibility(): Record<string, boolean> {
  return COLUMNS.reduce<Record<string, boolean>>((acc, column) => {
    acc[column.key] = column.defaultVisible !== false;
    return acc;
  }, {});
}

function loadColumnVisibility(): Record<string, boolean> {
  try {
    const stored = localStorage.getItem(VISIBILITY_STORAGE_KEY);
    if (stored) {
      return { ...getDefaultVisibility(), ...JSON.parse(stored) };
    }
  } catch {
    // Ignore parse errors.
  }
  return getDefaultVisibility();
}

function saveColumnVisibility(visibility: Record<string, boolean>) {
  localStorage.setItem(VISIBILITY_STORAGE_KEY, JSON.stringify(visibility));
}

function SortableHeader({
  label,
  sortKey,
  currentSortKey,
  sortDirection,
  onSort,
}: {
  label: string;
  sortKey: InstallationQualificationsFilters['sortKey'];
  currentSortKey?: InstallationQualificationsFilters['sortKey'];
  sortDirection?: 1 | -1;
  onSort: (key: InstallationQualificationsFilters['sortKey']) => void;
}) {
  const isActive = currentSortKey === sortKey;
  return (
    <Button
      variant="ghost"
      size="sm"
      className="-ml-3 h-8 hover:bg-transparent"
      onClick={() => onSort(sortKey)}
    >
      {label}
      {isActive ? (
        sortDirection === 1 ? <ArrowUp className="ml-1 h-3 w-3" /> : <ArrowDown className="ml-1 h-3 w-3" />
      ) : (
        <ArrowUpDown className="ml-1 h-3 w-3 opacity-50" />
      )}
    </Button>
  );
}

function AccountSearchFilter({
  value,
  onChange,
  options,
}: {
  value: string;
  onChange: (nextValue: string) => void;
  options: InstallationQualificationAccountOption[];
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    if (!search) return options;
    const query = search.toLowerCase();
    return options.filter((option) => {
      const label = `${option.accountNumber || ''} ${option.name}`.toLowerCase();
      return label.includes(query);
    });
  }, [options, search]);

  const selected = options.find((option) => option.id === value) || null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="h-7 w-full justify-between text-xs font-normal">
          <span className="truncate">
            {selected ? `${selected.accountNumber || ''} ${selected.name}`.trim() : '—'}
          </span>
          <ChevronsUpDown className="ml-1 h-3 w-3 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput placeholder="Search organisations..." value={search} onValueChange={setSearch} />
          <CommandList>
            <CommandEmpty className="py-2 text-center text-xs text-muted-foreground">No results</CommandEmpty>
            <CommandGroup className="max-h-56 overflow-y-auto">
              <CommandItem
                value="__all__"
                onSelect={() => {
                  onChange('');
                  setOpen(false);
                }}
                className="text-xs"
              >
                <Check className={cn('mr-2 h-4 w-4', !value ? 'opacity-100' : 'opacity-0')} />
                All organisations
              </CommandItem>
              {filtered.map((option) => (
                <CommandItem
                  key={option.id}
                  value={option.id}
                  onSelect={() => {
                    onChange(option.id);
                    setOpen(false);
                  }}
                  className="text-xs"
                >
                  <Check className={cn('mr-2 h-4 w-4', value === option.id ? 'opacity-100' : 'opacity-0')} />
                  <span className="truncate">
                    {option.accountNumber ? `${option.accountNumber} ` : ''}
                    {option.name}
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

interface InstallationQualificationsTableProps {
  rows: InstallationQualification[];
  total: number;
  page: number;
  pageSize: number;
  sortKey?: InstallationQualificationsFilters['sortKey'];
  sortDirection?: 1 | -1;
  columnFilters: Record<string, string>;
  accountOptions: InstallationQualificationAccountOption[];
  isLoading?: boolean;
  isRefreshing?: boolean;
  isExportingAll?: boolean;
  onSort: (key: InstallationQualificationsFilters['sortKey']) => void;
  onPageChange: (page: number) => void;
  onColumnFilterChange: (key: string, value: string) => void;
  onClearColumnFilters: () => void;
  onCopy: () => void;
  onExportPage: () => void;
  onExportAll: () => void;
  onRefresh: () => void;
}

export function InstallationQualificationsTable({
  rows,
  total,
  page,
  pageSize,
  sortKey,
  sortDirection = -1,
  columnFilters,
  accountOptions,
  isLoading,
  isRefreshing,
  isExportingAll,
  onSort,
  onPageChange,
  onColumnFilterChange,
  onClearColumnFilters,
  onCopy,
  onExportPage,
  onExportAll,
  onRefresh,
}: InstallationQualificationsTableProps) {
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>(loadColumnVisibility);
  const totalPages = Math.ceil(total / pageSize) || 1;
  const hasActiveColumnFilters = Object.values(columnFilters).some(Boolean);

  const visibleColumns = COLUMNS.filter((column) => columnVisibility[column.key] !== false);

  const toggleColumn = (key: string) => {
    const next = { ...columnVisibility, [key]: !columnVisibility[key] };
    setColumnVisibility(next);
    saveColumnVisibility(next);
  };

  const renderFilterCell = (column: ColumnDef) => {
    if (column.noFilter) return <TableHead key={`filter-${column.key}`} className="p-1" />;

    if (column.filterType === 'account-search') {
      return (
        <TableHead key={`filter-${column.key}`} className="p-1">
          <AccountSearchFilter
            value={columnFilters[column.key] || ''}
            onChange={(nextValue) => onColumnFilterChange(column.key, nextValue)}
            options={accountOptions}
          />
        </TableHead>
      );
    }

    if (column.filterType === 'select' && column.filterOptions) {
      return (
        <TableHead key={`filter-${column.key}`} className="p-1">
          <select
            className="h-7 w-full rounded-md border border-input bg-background px-2 text-xs"
            value={columnFilters[column.key] || ''}
            onChange={(event) => onColumnFilterChange(column.key, event.target.value)}
          >
            <option value="">All</option>
            {column.filterOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </TableHead>
      );
    }

    if (column.filterType === 'date-range') {
      const [from = '', to = ''] = (columnFilters[column.key] || '').split('>>');
      return (
        <TableHead key={`filter-${column.key}`} className="p-1">
          <div className="flex flex-col gap-1">
            <Input
              type="date"
              className="h-7 text-xs px-1"
              value={from}
              onChange={(event) => onColumnFilterChange(column.key, `${event.target.value}>>${to}`)}
            />
            <Input
              type="date"
              className="h-7 text-xs px-1"
              value={to}
              onChange={(event) => onColumnFilterChange(column.key, `${from}>>${event.target.value}`)}
            />
          </div>
        </TableHead>
      );
    }

    return (
      <TableHead key={`filter-${column.key}`} className="p-1">
        <Input
          className="h-7 text-xs"
          value={columnFilters[column.key] || ''}
          onChange={(event) => onColumnFilterChange(column.key, event.target.value)}
          placeholder="..."
        />
      </TableHead>
    );
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-sm text-muted-foreground">
          Showing {rows.length} of {total}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {hasActiveColumnFilters && (
            <Button variant="outline" size="sm" onClick={onClearColumnFilters}>
              <FilterX className="mr-1 h-4 w-4" />
              Clear filters
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={onCopy}>
            <Copy className="mr-1 h-4 w-4" />
            Copy
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" disabled={isExportingAll}>
                <Download className="mr-1 h-4 w-4" />
                {isExportingAll ? 'Exporting...' : 'CSV'}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>Export</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={onExportPage}>
                Export Page ({rows.length})
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onExportAll}>
                Export All ({total})
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button variant="outline" size="sm" onClick={onRefresh} disabled={isRefreshing}>
            <RefreshCw className={cn('mr-1 h-4 w-4', isRefreshing && 'animate-spin')} />
            Refresh
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm">
                <Settings2 className="mr-1 h-4 w-4" />
                Columns
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>Toggle columns</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {COLUMNS.filter((column) => !column.noFilter).map((column) => (
                <DropdownMenuCheckboxItem
                  key={column.key}
                  checked={columnVisibility[column.key] !== false}
                  onCheckedChange={() => toggleColumn(column.key)}
                >
                  {column.label}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <div className="overflow-x-auto rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/30 hover:bg-muted/30">
              {visibleColumns.map(renderFilterCell)}
            </TableRow>
            <TableRow>
              {visibleColumns.map((column) => (
                <TableHead
                  key={column.key}
                  className={cn(column.align === 'right' && 'text-right', column.className)}
                >
                  {column.sortKey ? (
                    <SortableHeader
                      label={column.label}
                      sortKey={column.sortKey}
                      currentSortKey={sortKey}
                      sortDirection={sortDirection}
                      onSort={onSort}
                    />
                  ) : (
                    column.label
                  )}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && Array.from({ length: 8 }).map((_, index) => (
              <TableRow key={`skeleton-${index}`}>
                {visibleColumns.map((column) => (
                  <TableCell key={`${column.key}-${index}`}>
                    <Skeleton className="h-4 w-full" />
                  </TableCell>
                ))}
              </TableRow>
            ))}

            {!isLoading && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={visibleColumns.length} className="py-12 text-center text-muted-foreground">
                  No installation qualifications found.
                </TableCell>
              </TableRow>
            )}

            {!isLoading && rows.map((row) => (
              <TableRow key={row.id} className="hover:bg-muted/50">
                {visibleColumns.map((column) => (
                  <TableCell
                    key={`${row.id}-${column.key}`}
                    className={cn(
                      'text-sm',
                      column.align === 'right' && 'text-right',
                      column.className,
                    )}
                  >
                    {column.render ? column.render(row) : (String(column.getValue(row) ?? '') || '—')}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between border-t px-1 pt-3">
          <p className="text-sm text-muted-foreground">
            Page {page + 1} of {totalPages}
          </p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => onPageChange(page - 1)} disabled={page === 0}>
              Previous
            </Button>
            <Button variant="outline" size="sm" onClick={() => onPageChange(page + 1)} disabled={page >= totalPages - 1}>
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
